import { ConfigurationError, NetworkError, ShopifyApiError } from './errors.ts';

import type {
  AdminApiClient,
  AdminApiClientConfig,
  AdminApiClientRequestOptions,
  GraphQLProxyInput,
  GraphQLProxyOutput,
  GraphQLResponse,
  TypedGraphQLDocument,
} from './types.ts';

const TAILOR_API_ENDPOINT = 'https://shopify-g4l5uulnln.erp.dev/query';

function validateConfig(config: AdminApiClientConfig): void {
  if (!config.storeDomain) {
    throw new ConfigurationError('storeDomain is required');
  }

  if (!config.apiVersion) {
    throw new ConfigurationError('apiVersion is required');
  }

  if (!config.accessToken) {
    throw new ConfigurationError('accessToken (integration token) is required');
  }

  // Validate integration token format
  if (!config.accessToken.startsWith('tst_')) {
    throw new ConfigurationError(
      'Invalid integration token format. Expected token starting with "tst_"'
    );
  }
}

/**
 * Type helper for GraphQL operations with typed variables and responses.
 *
 * @template TData - The expected response data type
 * @template TVariables - The expected variables type
 *
 * @example
 * const query = gql<ProductData, ProductVariables>`
 *   query GetProduct($id: ID!) {
 *     product(id: $id) {
 *       id
 *       title
 *     }
 *   }
 * `;
 *
 * // Then use with client.request:
 * const { data } = await client.request(query, { variables });
 * // TypeScript will infer ProductData and ProductVariables from the query
 */
export function gql<TData = unknown, TVariables = Record<string, unknown>>(
  strings: TemplateStringsArray,
  ...values: unknown[]
): TypedGraphQLDocument<TData, TVariables> {
  const query = strings.reduce(
    (acc, str, i) => acc + str + (values[i] ?? ''),
    ''
  );
  return {
    query,
  } as TypedGraphQLDocument<TData, TVariables>;
}

export function createAdminApiClient(
  config: AdminApiClientConfig
): AdminApiClient {
  validateConfig(config);

  return {
    request: async <TData = unknown, TVariables = Record<string, unknown>>(
      operation: string | TypedGraphQLDocument<TData, TVariables>,
      options?: AdminApiClientRequestOptions<TVariables>
    ): Promise<GraphQLResponse<TData>> => {
      const query = typeof operation === 'string' ? operation : operation.query;

      const input: GraphQLProxyInput = {
        integrationToken: config.accessToken,
        shopDomain: config.storeDomain,
        apiVersion: config.apiVersion,
        query,
        variables: options?.variables
          ? JSON.stringify(options.variables)
          : undefined,
        headers: options?.headers ? JSON.stringify(options.headers) : undefined,
      };

      const graphqlQuery = `
        mutation GraphQLProxy($input: GraphQLProxyInput!) {
          graphqlProxy(input: $input) {
            success
            data
            errors
            extensions
            userErrors
          }
        }
      `;

      try {
        // eslint-disable-next-line n/no-unsupported-features/node-builtins
        const response = await fetch(TAILOR_API_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            query: graphqlQuery,
            variables: { input },
          }),
        });

        if (!response.ok) {
          throw new NetworkError(
            `Network request failed with status ${response.status}`,
            response.status,
            response
          );
        }

        const result = (await response.json()) as {
          data?: { graphqlProxy?: GraphQLProxyOutput };
          errors?: unknown[];
        };

        // Check for GraphQL errors in the proxy request itself
        if (result.errors && result.errors.length > 0) {
          throw new NetworkError(
            'GraphQL proxy request failed',
            response.status,
            response
          );
        }

        const proxyResponse = result.data?.graphqlProxy as GraphQLProxyOutput;

        if (!proxyResponse) {
          throw new NetworkError(
            'Invalid response from proxy',
            response.status,
            response
          );
        }

        // Handle proxy-level errors (auth, validation, etc.)
        if (!proxyResponse.success) {
          let errors: unknown[] = [];
          let extensions: unknown;

          if (proxyResponse.errors) {
            try {
              errors = JSON.parse(proxyResponse.errors);
            } catch {
              errors = [{ message: proxyResponse.errors }];
            }
          }

          if (proxyResponse.extensions) {
            try {
              extensions = JSON.parse(proxyResponse.extensions);
            } catch {
              // Ignore parse errors for extensions
            }
          }

          if (errors.length > 0) {
            const firstError = errors[0] as { message?: string };
            throw new ShopifyApiError(
              firstError.message ?? 'Request failed',
              errors,
              extensions
            );
          }
          throw new ShopifyApiError('Request failed without specific error');
        }

        // Return the Shopify API response
        let shopifyData: TData | undefined;
        if (proxyResponse.data) {
          shopifyData = JSON.parse(proxyResponse.data) as TData;
        }

        const shopifyResponse: GraphQLResponse<TData> =
          shopifyData !== undefined ? { data: shopifyData } : {};

        if (proxyResponse.errors) {
          try {
            shopifyResponse.errors = JSON.parse(proxyResponse.errors);
          } catch {
            shopifyResponse.errors = [{ message: proxyResponse.errors }];
          }
        }

        if (proxyResponse.extensions) {
          try {
            shopifyResponse.extensions = JSON.parse(proxyResponse.extensions);
          } catch {
            // Ignore parse errors for extensions
          }
        }

        return shopifyResponse;
      } catch (error) {
        // Re-throw our custom errors
        if (error instanceof ShopifyApiError || error instanceof NetworkError) {
          throw error;
        }

        // Wrap other errors
        if (error instanceof Error) {
          throw new NetworkError(error.message);
        }

        throw new NetworkError('An unknown error occurred');
      }
    },
  };
}
